import { useId, type ChangeEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import { cx } from './primitives/cx';

/**
 * P-076 (P5) · Un renglón de selección: la casilla (o el radio) y su etiqueta juntos, en una fila que se toca
 * entera y mide al menos el objetivo táctil. Sustituye a las casillas sueltas de la piel anterior
 * (`checkbox-row`, `form-check`, `coverage-check`), que se deformaban dentro de un `.field` o quedaban sin
 * objetivo táctil.
 *
 * El `input` nativo sigue en el DOM —nombre accesible por su etiqueta, teclado, lector de pantalla, `name` y
 * estado controlado—; lo que se ve es la caja de al lado (`n3-check__box`), que ninguna regla de la piel
 * anterior alcanza. El estado marcado se pinta con una clase (Chromium 83 no entiende `:has()`).
 */
export interface CheckRowProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'checked' | 'className'> {
  type?: 'checkbox' | 'radio';
  checked: boolean;
  /** Recibe el estado nuevo; el evento, por si hace falta. */
  onChange: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  label: ReactNode;
  /** Una línea de ayuda bajo la etiqueta. Se anuncia junto con el control. */
  hint?: ReactNode;
  /** Sin caja alrededor: una casilla suelta (un filtro, una confirmación dentro de un aviso). */
  plain?: boolean;
  className?: string;
}

export function CheckRow({ type = 'checkbox', checked, onChange, label, hint, plain = false, className, disabled, 'aria-describedby': describedBy, ...rest }: CheckRowProps) {
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  // El nombre es solo la etiqueta (no la ayuda): por eso el control apunta a ella con `aria-labelledby`
  // en vez de heredar todo el texto del `label` que lo envuelve. La ayuda es la descripción.
  return (
    <label className={cx('n3-check', plain && 'n3-check--plain', checked && 'n3-check--on', disabled && 'n3-check--disabled', className)}>
      <input
        {...rest}
        aria-describedby={[describedBy, hint ? hintId : undefined].filter(Boolean).join(' ') || undefined}
        aria-labelledby={labelId}
        checked={checked}
        className="n3-check__input"
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked, event)}
        type={type}
      />
      <span aria-hidden="true" className={cx('n3-check__box', type === 'radio' && 'n3-check__box--radio')} />
      <span className="n3-check__text">
        <span className="n3-check__label" id={labelId}>{label}</span>
        {hint && <span className="n3-check__hint" id={hintId}>{hint}</span>}
      </span>
    </label>
  );
}

/**
 * Un grupo de renglones de selección: el título es el `legend` del `fieldset` (los lectores de pantalla lo
 * anuncian con cada opción), la ayuda va debajo y los renglones apilados. `scroll` acota una lista larga.
 */
export function CheckGroup({ legend, hint, scroll = false, children, className }: {
  legend: ReactNode;
  hint?: ReactNode;
  scroll?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <fieldset className={cx('n3-check-group', scroll && 'n3-check-group--scroll', className)}>
      <legend className="n3-check-group__legend">{legend}</legend>
      {hint && <p className="n3-check-group__hint">{hint}</p>}
      <div className="n3-check-group__items">{children}</div>
    </fieldset>
  );
}
